import { Button, Card, Row, Table } from "antd";
import { useCallback, useState } from "react";
import { useGetTableColumns } from "./hooks/useGetTableColumns";
import { useFetchDataCount } from "../../../../../../lib/hooks/useDefaultFetchDataCount";
import { TablePagination } from "../../../../../../components/TablePagination";
import { createPortal } from "react-dom";
import { useFetchData } from "../../../../../../lib/hooks/useDefaultFetchData";
import { useDefaultFormSidebar } from "../../../../../../lib/hooks/defaultFormSidebar";
import { TicklerForm } from "./TicklerForm";
import { TRequest } from "./types";
import { ITicklerExtended } from "../../../../../../types/Tickler";
import TicklersService from "../../../../../../services/accounts/ticklers";
import { useDefualtOnRow } from "../../../../../../lib/hooks/useDefaultOnRow";
import { usePermissions } from "../../../../../../lib/hooks/usePermissions";

type TicklersProps = {
  accountId: string;
  reloadAccount: () => void;
  sidebarIsOpen: boolean;
  setSidebarIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const Ticklers = ({ accountId, reloadAccount, sidebarIsOpen, setSidebarIsOpen }: TicklersProps) => {
  const [id, setId] = useState<string | null>(null);

  const { hasAccessByController } = usePermissions("Ticklers");

  const {
    request,
    isLoading: isCountLoading,
    total,
    onPageChange,
    fetchCount
  } = useFetchDataCount<{ ContractId: string }>(
    TicklersService,
    {
      ContractId: accountId
    },
    [""],
    50
  );

  const { data, isLoading, fetchData } = useFetchData<TRequest, ITicklerExtended>(
    TicklersService,
    request,
    ".ticklers-table .ant-table-content"
  );

  const reloadData = useCallback(() => {
    fetchCount();
    reloadAccount();
    fetchData();
  }, [fetchData, fetchCount, reloadAccount]);

  const { formWrapper, isOpen, setIsOpen } = useDefaultFormSidebar({
    id,
    setId,
    reloadData,
    title: "Tickler",
    parentId: accountId,
    FormElem: TicklerForm,
    outerIsOpen: sidebarIsOpen,
    setOuterIsOpen: setSidebarIsOpen
  });

  const openTickler = useCallback(
    (id: string) => {
      setIsOpen(true);
      setId(id);
    },
    [setIsOpen]
  );

  const { onRow } = useDefualtOnRow(openTickler);

  const { columns } = useGetTableColumns({
    setId,
    setIsOpen,
    reloadData,
    openTickler
  });

  const onAdd = useCallback(() => setIsOpen(true), [setIsOpen]);

  return (
    <Row className="w-100 h-100 px-8 py-6 card-table">
      <Card className="w-100 h-100">
        <Row className="w-100 d-flex justify-space-between">
          <h5 className="h h5 ant-table-title">Ticklers</h5>
          {hasAccessByController("Add") && (
            <Button shape="round" onClick={onAdd}>
              Add New
            </Button>
          )}
        </Row>
        <Row className="w-100 pt-6 d-block card-table__body">
          <Table
            dataSource={data}
            loading={isLoading}
            pagination={false}
            rowKey="id"
            columns={columns}
            className="w-100 h-100 clickable-rows-table small-table ticklers-table"
            onRow={onRow}
          />
          {total > 0 && (
            <TablePagination
              isTotalLoading={isCountLoading}
              total={total}
              currentPage={request.pageNumber}
              pageSize={request.pageSize}
              onPageChange={onPageChange}
            />
          )}
        </Row>
      </Card>
      {isOpen && createPortal(formWrapper, document.body)}
    </Row>
  );
};
